import React from 'react';
import PropTypes from 'prop-types';
import PartialTable from 'components/partials/PartialTable';
import { formatDateddMMyyyy, getMondayOfIsoWeek } from 'helpers/dateHelper';
import UsersInCellWidget from 'components/common/users/UsersInCellWidget';

const KpiSummaryTable = ({
  data,
  paging,
  total,
  nextPage,
  previousPage,
  gotoPage,
  handleClickRow,
  onPageSizeChange,
  isFromManagerPage
}) => {
  const baseCellStyle = {
    color: 'black',
    textAlign: 'center',
    padding: '10px',
    border: '1px solid #dee2e6'
  };

  const handleColor = (value, target) => {
    if (!target) {
      return <div style={{ padding: '5px' }}>{value}</div>;
    }
    const isExceeded = target <= value;
    const style = {
      ...baseCellStyle,
      backgroundColor: isExceeded ? '#ccf6e4' : '#fad7dd'
    };

    return (
      <div style={style}>
        <a
          data-tooltip-id="my-tooltip"
          data-tooltip-content={`Target : ${target}`}
        >
          {value}
        </a>
      </div>
    );
  };

  const columns = [
    {
      accessor: 'weekCommencing',
      Header: 'W/C date',
      Cell: rowData => {
        const { isTargetRow, label } = rowData.row.original;
        if (isTargetRow) {
          return (
            <div style={{ fontWeight: 'bold', color: '#d63384' }}>{label}</div>
          );
        }

        const { year, weekNumber } = rowData.row.original;
        const monday = getMondayOfIsoWeek(year, weekNumber);
        return formatDateddMMyyyy(monday);
      }
    },
    {
      accessor: 'weekT',
      Header: 'Year-Week',
      Cell: rowdata => {
        const { year, weekNumber, isTargetRow } = rowdata.row.original;
        if (isTargetRow) {
          return (
            <div style={{ fontWeight: 'bold', color: '#d63384' }}>-</div>
          );
        }
        return `${year}-${weekNumber}`;
      }
    },
    {
      accessor: 'totalVisits',
      Header: 'Total Visit P/W',
      Cell: (rowdata) => {
        const { totalVisits, target, isTargetRow } = rowdata.row.original;
        if (isTargetRow) {
          const total = (target?.prospect ?? 0) + (target?.existing ?? 0);
          return <strong>{total}</strong>;
        }
        const targetData = (target?.prospect ?? 0) + (target?.existing ?? 0);
        return handleColor(totalVisits, targetData);
      }
    },
    {
      accessor: 'prospectVisits',
      Header: 'Prospect Visit P/W',
      Cell: (rowdata) => {
        const { prospectVisits, target, isTargetRow } = rowdata.row.original;
        if (isTargetRow) return <strong>{target?.prospect ?? '-'}</strong>;
        return handleColor(prospectVisits, target?.prospect);
      }
    },
    {
      accessor: 'existingCustomerVisits',
      Header: 'Existing Customer Visit P/W',
      Cell: (rowdata) => {
        const { existingCustomerVisits, target, isTargetRow } = rowdata.row.original;
        if (isTargetRow) return <strong>{target?.existing ?? '-'}</strong>;
        return handleColor(existingCustomerVisits, target?.existing);
      }
    }
  ];

  if (isFromManagerPage) {
    columns.push({
      accessor: 'saleUserId',
      Header: 'Account Manager',
      Cell: rowData => {
        const { saleUserId, isTargetRow } = rowData.row.original;
        if (isTargetRow) return null;
        return (
          <UsersInCellWidget usersText={saleUserId?.toString()} showName />
        );
      }
    });
  }

  return (
    <PartialTable
      data={data}
      columns={columns}
      paging={paging}
      total={total}
      nextPage={nextPage}
      previousPage={previousPage}
      gotoPage={gotoPage}
      onPageSizeChange={onPageSizeChange}
      handleClickRow={handleClickRow}
    />
  );
};

KpiSummaryTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  paging: PropTypes.object,
  total: PropTypes.number,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  gotoPage: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  handleClickRow: PropTypes.func,
  isFromManagerPage: PropTypes.bool
};

export default KpiSummaryTable;
